import { useEffect, useRef, useState } from 'react'
import { useAuth } from './AuthContext.tsx'
import { AuthModal } from './AuthModal.tsx'
import { ProfileImage } from './ProfileImage.tsx'
import { Link, navigate } from './Router.tsx'
import { useEditorSettingsStore, useFontStore, useUserProfileMenuStore } from './store.ts'
import { availableFonts, themes, useThemeStore } from './theme.ts'

export function UserProfile() {
  const params = new URLSearchParams(window.location.search)
  if (params.has('noaccount')) {
    return <></>
  }

  const { user, signOut } = useAuth()
  const [showMenu, setShowMenu] = useState(false)
  const [showAuthModal, setShowAuthModal] = useState(false)
  const [showThemeSubmenu, setShowThemeSubmenu] = useState(false)
  const [showFontSubmenu, setShowFontSubmenu] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)
  const menuContainerRef = useRef<HTMLDivElement>(null)
  const themeSubmenuRef = useRef<HTMLDivElement>(null)
  const themeItemRef = useRef<HTMLDivElement>(null)
  const fontSubmenuRef = useRef<HTMLDivElement>(null)
  const fontItemRef = useRef<HTMLDivElement>(null)
  const submenuTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const fontSubmenuTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Use selectors to only subscribe to values, get setters non-reactively
  const currentTheme = useThemeStore(state => state.currentTheme)
  const getThemeStore = useThemeStore.getState
  const setTheme = getThemeStore().setTheme
  const setPreviewTheme = getThemeStore().setPreviewTheme

  const currentFont = useFontStore(state => state.currentFont)
  const getFontStore = useFontStore.getState
  const setFont = getFontStore().setFont
  const setPreviewFont = getFontStore().setPreviewFont

  const hideFunctionSignatures = useEditorSettingsStore(state => state.hideFunctionSignatures)
  const wordWrap = useEditorSettingsStore(state => state.wordWrap)
  const getEditorSettingsStore = useEditorSettingsStore.getState
  const setHideFunctionSignatures = getEditorSettingsStore().setHideFunctionSignatures
  const setWordWrap = getEditorSettingsStore().setWordWrap

  const getUserProfileMenuStore = useUserProfileMenuStore.getState
  const addCloseCallback = getUserProfileMenuStore().addCloseCallback
  const removeCloseCallback = getUserProfileMenuStore().removeCloseCallback

  useEffect(() => {
    const closeCallback = () => {
      setShowMenu(false)
      setShowThemeSubmenu(false)
      setShowFontSubmenu(false)
      setPreviewTheme(null)
      setPreviewFont(null)
    }
    addCloseCallback(closeCallback)
    return () => {
      removeCloseCallback(closeCallback)
    }
  }, [addCloseCallback, removeCloseCallback, setPreviewTheme, setPreviewFont])

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setShowMenu(false)
        setShowThemeSubmenu(false)
        setShowFontSubmenu(false)
        setPreviewTheme(null)
        setPreviewFont(null)
      }
    }

    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      if (submenuTimeoutRef.current) {
        clearTimeout(submenuTimeoutRef.current)
        submenuTimeoutRef.current = null
      }
      if (fontSubmenuTimeoutRef.current) {
        clearTimeout(fontSubmenuTimeoutRef.current)
        fontSubmenuTimeoutRef.current = null
      }
    }
  }, [showMenu])

  if (!user) {
    return (
      <>
        <button
          onClick={() => setShowAuthModal(true)}
          className="px-4 h-8 text-sm border-none rounded-md cursor-pointer font-semibold bg-gradient-to-br from-[#667eea] to-[#764ba2] text-white hover:opacity-90 flex items-center gap-2"
        >
          Sign In
        </button>
        {showAuthModal && <AuthModal onClose={() => setShowAuthModal(false)} />}
      </>
    )
  }

  return (
    <div className="relative" ref={menuRef}>
      <button onClick={() => setShowMenu(!showMenu)} className="cursor-pointer hover:opacity-90">
        <ProfileImage userId={user.id} userName={user.name} size={40} hasAvatar={user.hasAvatar} />
      </button>

      {showMenu && (
        <>
          <div
            ref={menuContainerRef}
            className="z-50 absolute right-0 top-14 flex flex-col bg-[#2a2a2a] border border-[#444] rounded-lg shadow-lg min-w-[200px] overflow-hidden select-none"
          >
            <div className="px-4 py-3 border-b border-[#444]">
              <div className="font-semibold">{user.name}</div>
              <div className="text-sm text-[#888]">{user.email}</div>
            </div>
            {user.isAdmin && (
              <Link
                href="/admin"
                onClick={() => setShowMenu(false)}
                className="w-full px-4 py-2 text-left hover:bg-[#333] cursor-pointer"
              >
                Admin
              </Link>
            )}
            <Link
              href={`/profile/${user.id}`}
              onClick={() => setShowMenu(false)}
              className="w-full px-4 py-2 text-left hover:bg-[#333] cursor-pointer"
            >
              My Profile
            </Link>
            <div
              ref={themeItemRef}
              className="relative"
              onMouseEnter={() => {
                if (submenuTimeoutRef.current) {
                  clearTimeout(submenuTimeoutRef.current)
                  submenuTimeoutRef.current = null
                }
                setShowThemeSubmenu(true)
              }}
              onMouseLeave={e => {
                const relatedTarget = e.relatedTarget as HTMLElement
                if (themeSubmenuRef.current?.contains(relatedTarget)) {
                  return
                }
                submenuTimeoutRef.current = setTimeout(() => {
                  if (!themeSubmenuRef.current?.matches(':hover')) {
                    setShowThemeSubmenu(false)
                    setPreviewTheme(null)
                  }
                }, 100)
              }}
            >
              <div className="w-full px-4 py-2 text-left hover:bg-[#333] cursor-pointer flex items-center justify-between">
                <span>Theme</span>
                <div className="flex items-center gap-2">
                  <div
                    className="w-4 h-4 rounded border border-[#555]"
                    style={{ backgroundColor: themes[currentTheme]?.colors.function || '#000000' }}
                  />
                  <span className="text-xs text-[#888] capitalize">{currentTheme}</span>
                </div>
              </div>
            </div>
            <div
              ref={fontItemRef}
              className="relative"
              onMouseEnter={() => {
                if (fontSubmenuTimeoutRef.current) {
                  clearTimeout(fontSubmenuTimeoutRef.current)
                  fontSubmenuTimeoutRef.current = null
                }
                setShowFontSubmenu(true)
              }}
              onMouseLeave={e => {
                const relatedTarget = e.relatedTarget as HTMLElement
                if (fontSubmenuRef.current?.contains(relatedTarget)) {
                  return
                }
                fontSubmenuTimeoutRef.current = setTimeout(() => {
                  if (!fontSubmenuRef.current?.matches(':hover')) {
                    setShowFontSubmenu(false)
                  }
                }, 100)
              }}
            >
              <div className="w-full px-4 py-2 text-left hover:bg-[#333] cursor-pointer flex items-center justify-between">
                <span>Font</span>
                <span className="text-xs text-[#888]" style={{ fontFamily: `"${currentFont}"` }}>
                  {currentFont}
                </span>
              </div>
            </div>
            <div className="px-4 py-2">
              <label className="inline-flex items-center cursor-pointer gap-2 text-sm w-full">
                <span className="flex-1">Function Signatures</span>
                <span className="text-xs text-[#888]">Alt+f</span>
                <input
                  type="checkbox"
                  checked={!hideFunctionSignatures}
                  onChange={e => setHideFunctionSignatures(!e.target.checked)}
                  className="sr-only peer"
                />
                <div
                  className={`relative w-12 h-6 rounded-full transition-colors ${
                    !hideFunctionSignatures
                      ? 'bg-gradient-to-br from-green-600 to-lime-400'
                      : 'bg-gradient-to-br from-neutral-900 to-neutral-700'
                  }`}
                >
                  <div
                    className={`absolute top-[2px] bg-white text-neutral-500 border border-gray-300 rounded-full w-5 h-5 transition-transform duration-200 ${
                      !hideFunctionSignatures
                        ? 'left-[2px] translate-x-[24px]'
                        : 'right-[2px] translate-x-[-24px]'
                    }`}
                  />
                </div>
              </label>
            </div>
            <div className="px-4 py-2">
              <label className="inline-flex items-center cursor-pointer gap-2 text-sm w-full">
                <span className="flex-1">Word Wrap</span>
                <span className="text-xs text-[#888]">Alt+o</span>
                <input
                  type="checkbox"
                  checked={wordWrap}
                  onChange={e => setWordWrap(e.target.checked)}
                  className="sr-only peer"
                />
                <div
                  className={`relative w-12 h-6 rounded-full transition-colors ${
                    wordWrap
                      ? 'bg-gradient-to-br from-green-600 to-lime-400'
                      : 'bg-gradient-to-br from-neutral-900 to-neutral-700'
                  }`}
                >
                  <div
                    className={`absolute top-[2px] bg-white text-neutral-500 border border-gray-300 rounded-full w-5 h-5 transition-transform duration-200 ${
                      wordWrap
                        ? 'left-[2px] translate-x-[24px]'
                        : 'right-[2px] translate-x-[-24px]'
                    }`}
                  />
                </div>
              </label>
            </div>
            <button
              onClick={async () => {
                await signOut()
                setShowMenu(false)
              }}
              className="w-full px-4 py-2 text-left hover:bg-[#333] cursor-pointer"
            >
              Sign Out
            </button>
          </div>
          {showThemeSubmenu && themeItemRef.current && menuContainerRef.current && (
            <div
              ref={themeSubmenuRef}
              className="z-[60] absolute flex flex-col bg-[#2a2a2a] border border-[#444] rounded-lg shadow-lg min-w-[180px] overflow-hidden"
              style={{
                right: `${menuContainerRef.current.offsetWidth}px`,
                top: `${55 + themeItemRef.current.offsetTop}px`,
              }}
              onMouseEnter={() => {
                if (submenuTimeoutRef.current) {
                  clearTimeout(submenuTimeoutRef.current)
                  submenuTimeoutRef.current = null
                }
                setShowThemeSubmenu(true)
              }}
              onMouseLeave={e => {
                const relatedTarget = e.relatedTarget as HTMLElement
                if (themeItemRef.current?.contains(relatedTarget)) {
                  return
                }
                setShowThemeSubmenu(false)
                setPreviewTheme(null)
              }}
            >
              {Object.keys(themes).map(themeName => (
                <button
                  key={themeName}
                  onMouseEnter={() => setPreviewTheme(themeName)}
                  onMouseLeave={() => setPreviewTheme(null)}
                  onClick={() => {
                    setTheme(themeName)
                    setShowThemeSubmenu(false)
                    setShowMenu(false)
                    setPreviewTheme(null)
                  }}
                  className={`w-full px-4 py-2 text-left hover:bg-[#333] cursor-pointer ${
                    currentTheme === themeName ? 'bg-[#333]' : ''
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <div
                      className="w-4 h-4 rounded border border-[#555]"
                      style={{ backgroundColor: themes[themeName]!.colors.function }}
                    />
                    <span className="capitalize">{themeName}</span>
                  </div>
                </button>
              ))}
            </div>
          )}
          {showFontSubmenu && fontItemRef.current && menuContainerRef.current && (
            <div
              ref={fontSubmenuRef}
              className="z-[60] absolute flex flex-col bg-[#2a2a2a] border border-[#444] rounded-lg shadow-lg min-w-[180px] overflow-hidden"
              style={{
                right: `${menuContainerRef.current.offsetWidth}px`,
                top: `${55 + fontItemRef.current.offsetTop}px`,
              }}
              onMouseEnter={() => {
                if (fontSubmenuTimeoutRef.current) {
                  clearTimeout(fontSubmenuTimeoutRef.current)
                  fontSubmenuTimeoutRef.current = null
                }
                setShowFontSubmenu(true)
              }}
              onMouseLeave={e => {
                const relatedTarget = e.relatedTarget as HTMLElement
                if (fontItemRef.current?.contains(relatedTarget)) {
                  return
                }
                setShowFontSubmenu(false)
                setPreviewFont(null)
              }}
            >
              {availableFonts.map(fontName => (
                <button
                  key={fontName}
                  onMouseEnter={() => setPreviewFont(fontName)}
                  onMouseLeave={() => setPreviewFont(null)}
                  onClick={() => {
                    setFont(fontName)
                    setShowFontSubmenu(false)
                    setShowMenu(false)
                    setPreviewFont(null)
                  }}
                  className={`w-full px-4 py-2 text-left hover:bg-[#333] cursor-pointer ${
                    currentFont === fontName ? 'bg-[#333]' : ''
                  }`}
                  style={{ fontFamily: `"${fontName}"` }}
                >
                  {fontName}
                </button>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  )
}
